import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import cloneDeep from 'clone-deep';

import GreenButton from '../../common/styled/GreenButton/GreenButton';
import CustomLoader from '../../components/CustomLoader/CustomLoader';
import Language, { EmptyLanguage } from '../../types/Language';
import api from '../../utils/api';
import snackbar from '../../utils/snackbar';
import TranslationTable from './components/TranslationTable/TranslationTable';
import { FlexContainer, Title } from './styled';

const TranslationPage = () => {
    const { id: languageId } = useParams<{id: string}>();

    const { t } = useTranslation();
    const history = useHistory();

    const [isLoading, setIsLoading] = useState(true);
    const [languageData, setLanguageData] = useState<Language>(EmptyLanguage);
    const [baseTranslation, setBaseTranslatiom] = useState<any>({});
    const [currentTranslation, setCurrentTranslation] = useState<any>({});

    useEffect(() => {
        api.request('/languages/' + languageId).then(res => {
            setLanguageData(res);
        }).catch(() => {
            history.push('/languages');
        });
    }, [languageId, history]);

    useEffect(() => {
        if (languageData.id === 0) return;
        api.request('/locales/it-IT', 'GET', null, true, 'text').then((resBase) => {
            setBaseTranslatiom(JSON.parse(resBase));
            api.request('/locales/' + languageData.code, 'GET', { remove_null: false }, true, 'text').then((resCurrent) => {
                setCurrentTranslation(JSON.parse(resCurrent));
                setIsLoading(false);
            });
        });
    }, [languageData]);

    const setNestedKey = useCallback((obj: any, path: string[], value: string) => {
        // this method set an object value with a dynamic made key
        if (path.length === 1) {
            obj[path[0]] = value;
            return;
        }
        if (obj[path[0]] === undefined) obj[path[0]] = {};
        setNestedKey(obj[path[0]], path.slice(1), value);
    }, []);

    const handleAttributeChange = useCallback((attribute: string, value: any) => {
        setCurrentTranslation((curr: any) => {
            const newState = cloneDeep(curr);
            setNestedKey(newState, attribute.split('.'), value);
            return newState;
        });
    }, [setNestedKey]);

    const saveData = useCallback(() => {
        api.request('/locales/' + languageData.code, 'PUT', { translation: currentTranslation }, true, 'text').then(() => {
            snackbar.success(t('pages.language_translation.translation_saved'));
        });
    }, [currentTranslation, languageData.code, t]);

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <FlexContainer>
                <Title variant='h4'>
                    {t('pages.language_translation.title', { language: languageData.description })}
                </Title>
                <GreenButton variant='outlined' onClick={saveData}>
                    {t('pages.language_translation.translation_save')}
                </GreenButton>
            </FlexContainer>
            <TranslationTable base={baseTranslation} translation={currentTranslation} handleAttributeChange={handleAttributeChange} />
        </>
    );
};

export default TranslationPage;
